import {
  BentoBrand,
  BentoContact,
  BentoContactResponseType,
  BrandContact,
  Organization,
  OrganizationProfile,
  ProfileType,
  Subscription,
  SubscriptionType,
} from "schemas/dashboard";
import { Map, StringMap } from "schemas/functions";

import {
  CAMPAIGN_AB_ACCESS,
  CAMPAIGN_AB_CONTROL,
  CAMPAIGN_AB_PAID,
} from "constants/trackingProps";

import { ON_BEHALF_OF_TOKEN } from "./localStorage";

export const REFERRAL_CREDIT = 20;

export const getBrandContactName = (contact: BrandContact) => {
  return contact?.name || contact?.title || contact?.email || "";
};

export const hasSocialMediaProfile = (
  organizationProfile: OrganizationProfile | StringMap | null,
) => {
  if (!organizationProfile) {
    return false;
  }
  return (
    organizationProfile?.instagramUrl ||
    organizationProfile?.tiktokUrl ||
    organizationProfile?.youtubeUrl ||
    organizationProfile?.twitchUrl ||
    organizationProfile?.portfolioUrl
  );
};

export const getProfilePlatform = (
  organizationProfile: OrganizationProfile | null,
) => {
  if (organizationProfile?.instagramUrl) {
    return { platform: "Instagram " };
  } else if (organizationProfile?.tiktokUrl) {
    return { platform: "Tiktok " };
  } else if (organizationProfile?.youtubeUrl) {
    return { platform: "YouTube " };
  } else if (organizationProfile?.twitchUrl) {
    return { platform: "Twitch " };
  } else {
    return { platform: "" };
  }
};

export const accessToPartnershipData = (
  currentOrg: Organization | null,
  subscription?: Subscription | null,
) => {
  return (
    currentOrg?.isVaUser ||
    currentOrg?.isAutopilotUser ||
    subscription?.partnershipData?.status === SubscriptionType.ACTIVE
  );
};

export const hasCampaignAbAccess = (currentOrg: Organization | null) => {
  if (currentOrg?.isPaidUser) {
    return true;
  }
  return !!currentOrg?.id && currentOrg.id % 2 === 1;
};

export const getCampaignAbGroup = (currentOrg: Organization | null) => {
  if (currentOrg?.isPaidUser) {
    return CAMPAIGN_AB_PAID;
  }
  return hasCampaignAbAccess(currentOrg)
    ? CAMPAIGN_AB_ACCESS
    : CAMPAIGN_AB_CONTROL;
};

export const contactReplied = (bentoContact: BentoContact) => {
  return (
    !!bentoContact.bestResponseType &&
    bentoContact.bestResponseType !==
      BentoContactResponseType.no_content_marketing &&
    bentoContact.bestResponseType !== BentoContactResponseType.bounced &&
    bentoContact.bestResponseType !== BentoContactResponseType.automated &&
    bentoContact.bestResponseType !== BentoContactResponseType.wrong_contact &&
    bentoContact.bestResponseType !== BentoContactResponseType.stop_emailing &&
    bentoContact.bestResponseType !== BentoContactResponseType.not_in_company
  );
};

export const contactAskForRates = (bentoContact: BentoContact) => {
  return (
    bentoContact.bestResponseType ===
    BentoContactResponseType.cash_compensation_discussion
  );
};

export const contactDiscussedCollab = (bentoContact: BentoContact) => {
  return (
    bentoContact.bestResponseType ===
      BentoContactResponseType.details_discussion ||
    bentoContact.bestResponseType ===
      BentoContactResponseType.compensation_discussion ||
    bentoContact.bestResponseType === BentoContactResponseType.schedule_call
  );
};

export const contactShowedInterest = (bentoContact: BentoContact) => {
  return (
    bentoContact.bestResponseType === BentoContactResponseType.shared_profile ||
    bentoContact.bestResponseType ===
      BentoContactResponseType.referred_someone_else ||
    bentoContact.bestResponseType ===
      BentoContactResponseType.reach_out_later ||
    bentoContact.bestResponseType ===
      BentoContactResponseType.not_interested_reach_out_later ||
    bentoContact.bestResponseType ===
      BentoContactResponseType.only_tiktok_shop ||
    bentoContact.bestResponseType ===
      BentoContactResponseType.products_or_services_only ||
    bentoContact.bestResponseType ===
      BentoContactResponseType.only_influencer_marketing ||
    bentoContact.bestResponseType === BentoContactResponseType.send_socials ||
    bentoContact.bestResponseType === BentoContactResponseType.wrong_location ||
    bentoContact.bestResponseType === BentoContactResponseType.interested
  );
};

export const getSourceLabel = (bentoBrand: BentoBrand) => {
  const isAdmin = Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));

  if (bentoBrand?.isAdmin) {
    return "From Virtual Assistant";
  } else if (bentoBrand?.createdByAutomation) {
    return "From Automation";
  } else {
    return isAdmin ? "From User" : "";
  }
};

export const getLocation = (profile?: OrganizationProfile | null) => {
  let formattedString = "";
  if (profile?.addressComponents) {
    const city = profile?.addressComponents?.find((x) =>
      x.types?.includes("locality"),
    )?.longText;
    if (city) {
      formattedString += city;
    }
    const country = profile?.addressComponents?.find((x) =>
      x.types?.includes("country"),
    )?.longText;
    if (country) {
      if (city) {
        formattedString += `, ${country}`;
      } else {
        formattedString += country;
      }
    }
  }
  return formattedString;
};

export const isUgcCreator = (profile: OrganizationProfile | Map | null) => {
  if (!profile) return false;
  return (
    profile?.profileTypes &&
    profile?.profileTypes?.includes(ProfileType.ugcCreator)
  );
};

export const isInfluencer = (profile: OrganizationProfile | Map | null) => {
  if (!profile) return false;
  return (
    profile?.profileTypes &&
    profile?.profileTypes?.includes(ProfileType.influencer)
  );
};
